import { useEffect, useState } from "react";
import type { VnProgram } from "../engine/Interpreter";
import { setAssetRoot } from "../engine/assetRoot";
import { IDBSaveStorage, type SaveStorage } from "../engine/SaveLoad";
import { AchievementStore, type AchievementSink } from "../engine/AchievementStore";
import type { AuthResult, CatalogueEntry, GrantPayload, PlatformAdapter, Session } from "./PlatformAdapter";

// Production fallback: Vite build-time scan of all *.vnp.json game files baked
// into the public/json/ folder. In dev we instead ask the live compiler server
// (see useLiveScriptNames below) so newly-added .arc files appear without a build.
const gameFiles = import.meta.glob("../../public/json/*.vnp.json");
const staticScriptNames = Object.keys(gameFiles)
  .map((path) => {
    const match = path.match(/\/([^/]+)\.vnp\.json$/);
    return match ? match[1] : "";
  })
  .filter(Boolean);

/**
 * The list of selectable games.
 *
 * In dev, the .vnp.json files are compiled in-memory by `arc-compile serve` and
 * never written to disk, so the static glob above can't see new ones. Instead we
 * fetch the server's list endpoint (`/json/` → `{ scripts: [...] }`, proxied to
 * port 8765 by vite.config.ts). In a production build there is no server, so we
 * keep the build-time glob.
 */
function useLiveScriptNames(): string[] {
  const [names, setNames] = useState<string[]>(staticScriptNames);

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    // The compiler server compiles every .arc on startup before it answers, so
    // the run script's browser-open can easily beat it to the punch. A single
    // fetch would then catch the Vite proxy's "server not up" stub (which has no
    // `scripts` key) and silently strand us on the stale static list. Poll until
    // the server returns a real list, then stop. (Without this, freshly-added
    // games like demo_shelves never appear unless the static glob happens to
    // include them.)
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    const MAX_ATTEMPTS = 40; // ~30s at 750ms — covers a cold compile of every .arc

    const scheduleRetry = () => {
      if (cancelled || attempts >= MAX_ATTEMPTS) return;
      attempts += 1;
      timer = setTimeout(poll, 750);
    };

    const poll = () => {
      fetch("/json/")
        .then((res) => (res.ok ? res.json() : null))
        .then((data: { scripts?: string[] } | null) => {
          if (cancelled) return;
          if (Array.isArray(data?.scripts)) {
            setNames(data.scripts); // got the live list — stop polling
          } else {
            scheduleRetry(); // proxy stub / server not ready yet
          }
        })
        .catch(() => {
          if (!cancelled) scheduleRetry();
        });
    };

    poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  return names;
}

function useCatalogue(): CatalogueEntry[] {
  const names = useLiveScriptNames();
  return names.map((id) => ({ id, title: id, status: "available" as const }));
}

async function loadGame(id: string): Promise<VnProgram> {
  const res = await fetch(`/json/${id}.vnp.json`);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  // Per-game asset root (design §3.4/§5.1). The dev server serves
  // games/{id}/assets/ here when the game has its own tree, and falls back to
  // the games/_library/ checkout for engine fixtures — either way this URL
  // prefix is valid for every game the /json/ catalogue lists.
  setAssetRoot(`/games-dev/${id}/assets`);
  return res.json() as Promise<VnProgram>;
}

function createSaveStorage(_gameId: string): SaveStorage {
  return new IDBSaveStorage();
}

// No account concept locally — always "authenticated" so login UI never shows.
function useSession(): Session {
  return { status: "authenticated" };
}

async function noopAuth(): Promise<AuthResult> {
  return { ok: true };
}

async function logout(): Promise<void> {}

// No account concept locally — nothing to reset/verify/request.
async function forgotPassword(_email: string): Promise<void> {}

// A single shared instance is fine — IndexedDB is already the shared backend
// regardless of how many callers ask for a sink.
const localAchievementStore = new AchievementStore();
function achievementSink(): AchievementSink {
  return localAchievementStore;
}

// No grants concept locally — every VAR decl just uses its own declared default.
async function fetchGrants(_gameId: string): Promise<GrantPayload | null> {
  return null;
}

// No session to validate locally — nothing to connect, nothing to disconnect.
function connectHeartbeat(_gameId: string, _onSessionInvalid: () => void): () => void {
  return () => {};
}

// No Prelude concept locally — every local script is already unauthenticated-playable.
async function fetchPrelude(): Promise<CatalogueEntry | null> {
  return null;
}

export const localPlatform: PlatformAdapter = {
  useCatalogue,
  loadGame,
  createSaveStorage,
  useSession,
  login: noopAuth,
  register: noopAuth,
  logout,
  resetPassword: noopAuth,
  verifyEmail: noopAuth,
  forgotPassword,
  achievementSink,
  fetchGrants,
  connectHeartbeat,
  fetchPrelude,
};
